import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, badgeClassName } from '@/components/atoms/Badge';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/molecules/Menu';
import type { AlertSilence, AlertUnsilence } from '@/domain/alerts';
import {
    bioscopeAlertCodes,
    isUserActionRequired,
} from '@/routes/Internal/routes/Dashboard/components/Alert/bioscopeAlertCodes';

import styles from '../BioscopeListContainer.module.scss';

interface AlertItem {
    id: number;
    alertCode: string;
    acknowledged: boolean;
}

interface AlertLocation {
    id: number;
}

interface AlertActions {
    alertMap: Record<string, string>;
    onSilence: (silence: AlertSilence) => void;
    onUnsilence: (unsilence: AlertUnsilence) => void;
}

interface AlertDescriptionProps extends AlertActions {
    alert: AlertItem;
    location?: AlertLocation;
}

function AlertDescription(props: AlertDescriptionProps) {
    const { alert, location, alertMap, onSilence, onUnsilence } = props;
    const { t } = useTranslation();
    const code = alert.alertCode;
    const locationId = location?.id;
    const [showAlertMenu, setShowAlertMenu] = useState(false);
    const timePeriods = [
        {
            label: t('internal.bioscopeList.silence.oneDay'),
            value: (date: Date): Date | null => {
                date.setDate(date.getDate() + 1);
                return date;
            },
        },
        {
            label: t('internal.bioscopeList.silence.oneWeek'),
            value: (date: Date): Date | null => {
                date.setDate(date.getDate() + 7);
                return date;
            },
        },
        {
            label: t('internal.bioscopeList.silence.oneMonth'),
            value: (date: Date): Date | null => {
                date.setMonth(date.getMonth() + 1);
                return date;
            },
        },
        { label: t('internal.bioscopeList.silence.untilNewPen'), value: (): Date | null => null },
    ];
    // A silence is stored per location, so a bioscope on land has nothing to silence: the
    // request would carry no location and fail on the server.
    const silence = (until: string | undefined) => {
        if (locationId === undefined) return;
        onSilence({ alertCode: code, locationId, until, alertId: alert.id });
    };
    const silenceActions = timePeriods.map((period) => ({
        label: period.label,
        onClick: () => silence(period.value(new Date())?.toISOString()),
    }));
    silenceActions.push({
        label: t('internal.bioscopeList.silence.untilHealed'),
        onClick: () => silence(undefined),
    });
    const unsilenceActions = [
        {
            label: t('internal.bioscopeList.silence.unsilence'),
            onClick: () => {
                if (locationId === undefined) return;
                onUnsilence({ alertCode: code, locationId });
            },
        },
    ];
    const message = alertMap[code];
    return (
        <Menu isOpen={showAlertMenu} onOpenChange={setShowAlertMenu}>
            <MenuTrigger
                variant="unstyled"
                caret
                className={
                    alert.acknowledged
                        ? styles.alertToggle
                        : badgeClassName(
                              {
                                  tone: isUserActionRequired(code) ? 'danger' : 'warning',
                                  size: 'md',
                              },
                              styles.alertBadgeToggle
                          )
                }>
                {message}{' '}
            </MenuTrigger>
            <MenuContent>
                {(alert.acknowledged ? unsilenceActions : silenceActions).map((item) => (
                    <MenuItem key={item.label} onClick={item.onClick}>
                        {item.label}
                    </MenuItem>
                ))}
            </MenuContent>
        </Menu>
    );
}

interface AlertsContainerProps extends AlertActions {
    alerts: AlertItem[];
    location?: AlertLocation;
}

/** A bioscope's open alerts, unsilenced first, each opening a menu to silence or unsilence it. */
export default function AlertsContainer(props: AlertsContainerProps) {
    const { alerts, location, alertMap, onSilence, onUnsilence } = props;
    const { t } = useTranslation();

    if (alerts.length === 0) {
        return (
            <Badge tone="success" size="md">
                {t('common.labels.allGood')}
            </Badge>
        );
    }
    return (
        <>
            {alerts
                .sort(
                    (a, b) =>
                        Number(a.acknowledged) - Number(b.acknowledged) ||
                        bioscopeAlertCodes.indexOf(a?.alertCode) -
                            bioscopeAlertCodes.indexOf(b?.alertCode)
                )
                .map((alert) => (
                    <AlertDescription
                        key={alert?.alertCode}
                        alert={alert}
                        location={location}
                        alertMap={alertMap}
                        onSilence={onSilence}
                        onUnsilence={onUnsilence}
                    />
                ))}
        </>
    );
}
